import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown, Plus, Users } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { useLanguage } from '@/shared/hooks/use-language';
import { ScopeContextStorage } from '@/shared/lib/scope-storage';
import { DebugLogger } from '@/shared/lib/debug';
import { getAccountHolders } from '../api/account-holder';
import { getActiveAccountsForHolder, getAllAccounts } from '../api/account';
import type { Account, ScopeContext } from '../model/types';

interface ScopeSwitcherProps {
  sessionId: string | null;
  scopeContext: ScopeContext | null;
  onNavigateAway?: (to: string, state?: unknown) => void;
}

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

const SKELETON_ROWS = 3;

export function ScopeSwitcher({ sessionId, scopeContext, onNavigateAway }: ScopeSwitcherProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const exitTo = (to: string, state?: unknown) => {
    if (onNavigateAway) {
      onNavigateAway(to, state);
      return;
    }
    navigate(to, { state });
  };
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [holderCount, setHolderCount] = useState<number | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const currentAccountRef = useRef<HTMLDivElement | null>(null);
  const accountsLabelId = useId();

  const account = scopeContext?.account;
  const accountHolder = scopeContext?.accountHolder;

  useEffect(() => {
    if (loadState !== 'loaded') {
      return;
    }

    const frame = requestAnimationFrame(() => {
      currentAccountRef.current?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [loadState]);

  const loadOptions = async () => {
    if (!sessionId) {
      return;
    }

    setLoadState('loading');

    try {
      const [holdersResult, accountsResult] = await Promise.allSettled([
        getAccountHolders(sessionId, 'active'),
        accountHolder
          ? getActiveAccountsForHolder(sessionId, accountHolder.id)
          : getAllAccounts(sessionId, 'active').then((response) => response.accounts),
      ]);

      if (accountsResult.status === 'rejected' || !Array.isArray(accountsResult.value)) {
        DebugLogger.error('Failed to load the scope switcher accounts', {
          sessionId,
          accountsError: accountsResult.status === 'rejected'
            ? String(accountsResult.reason)
            : 'unexpected accounts payload shape',
        });
        setLoadState('error');
        return;
      }

      if (holdersResult.status === 'rejected') {
        DebugLogger.error('Failed to load the scope switcher holders, offering a retry', {
          sessionId,
          holdersError: String(holdersResult.reason),
        });
      }

      setHolderCount(holdersResult.status === 'fulfilled' ? holdersResult.value.holders.length : null);
      setAccounts(accountsResult.value);
      setLoadState('loaded');
    } catch (error) {
      DebugLogger.error('Failed to load the scope switcher options', {
        sessionId,
        error: String(error),
      });
      setLoadState('error');
    }
  };

  const retryHolders = async () => {
    if (!sessionId) {
      return;
    }

    try {
      const response = await getAccountHolders(sessionId, 'active');
      setHolderCount(response.holders.length);
    } catch (error) {
      DebugLogger.error('Retrying the scope switcher holders failed', {
        sessionId,
        error: String(error),
      });
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open && loadState !== 'loading') {
      loadOptions();
    }
  };

  const handleAccountSelect = (nextAccount: Account) => {
    if (!sessionId || nextAccount.id === account?.id) {
      return;
    }

    const nextContext: ScopeContext = {
      ...scopeContext,
      account: nextAccount,
      scopeId: nextAccount.id,
      scopeType: 'account',
    };

    DebugLogger.scope('Switching account from the sidebar', {
      sessionId,
      previousAccountId: account?.id,
      accountId: nextAccount.id,
      accountName: nextAccount.name,
    });

    ScopeContextStorage.storeScopeContext(sessionId, nextContext);
    window.location.href = '/console';
  };

  const handleChangeAccountHolder = () => {
    exitTo('/account-holder-selection', { sessionId, reselect: true });
  };

  const handleCreateAccount = () => {
    exitTo('/account-creation', {
      sessionId,
      scopeContext,
      holderId: accountHolder?.id ?? account?.holder_id,
      origin: 'console',
    });
  };

  if (!sessionId || (!account && !accountHolder)) {
    return null;
  }

  const currentName = account?.name ?? accountHolder!.name;
  const activeAccounts = accounts.filter((item) => item.status === 'active');
  const hasAccountChoice = activeAccounts.some((item) => item.id !== account?.id);
  const holdersUnknown = holderCount === null;
  const canChangeAccountHolder = holderCount !== null && holderCount > 1;
  const holderId = accountHolder?.id ?? account?.holder_id;
  const canCreateAccount = Boolean(holderId);

  const createAccountItem = canCreateAccount ? (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        data-testid="scope-switcher-create-account"
        className="cursor-pointer"
        onSelect={handleCreateAccount}
      >
        <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
        {t('sidebar.createAccount')}
      </DropdownMenuItem>
    </>
  ) : null;

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="scope-switcher"
          className="w-full flex items-start gap-3 px-2 py-2 rounded-lg bg-sidebar-accent/50 text-left transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar"
        >
          <Building2 className="w-5 h-5 mt-0.5 text-sidebar-foreground flex-shrink-0" />
          <span className="block flex-1 min-w-0">
            <span className="block text-xs font-medium text-sidebar-foreground/70 mb-1">
              {account ? t('sidebar.currentAccount') : t('sidebar.currentAccountHolder')}
            </span>
            <span
              className="block text-sm font-semibold text-sidebar-foreground truncate"
              title={currentName}
              data-testid="scope-switcher-current"
            >
              {currentName}
            </span>
            {account && accountHolder && (
              <span className="block text-xs text-sidebar-foreground/70 truncate" title={accountHolder.name}>
                {accountHolder.name}
              </span>
            )}
          </span>
          <ChevronsUpDown className="w-4 h-4 mt-0.5 text-sidebar-foreground/70 flex-shrink-0" />
          <span className="sr-only">{t('sidebar.switchAccount')}</span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent side="top" align="start" className="w-56">
        {(loadState === 'idle' || loadState === 'loading') && (
          <>
            <DropdownMenuItem disabled className="text-xs text-muted-foreground data-[disabled]:opacity-100" data-testid="scope-switcher-loading">
              {t('sidebar.loadingAccounts')}
            </DropdownMenuItem>
            {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
              <div key={`skeleton-${index}`} aria-hidden="true" className="px-2 py-1.5">
                <div className="skeleton-shimmer h-3 rounded bg-muted" />
              </div>
            ))}
          </>
        )}

        {loadState === 'error' && (
          <>
            <DropdownMenuItem disabled className="text-xs text-destructive data-[disabled]:opacity-100" data-testid="scope-switcher-error">
              {t('sidebar.accountsError')}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="scope-switcher-retry"
              className="cursor-pointer"
              onSelect={(event) => {
                event.preventDefault();
                loadOptions();
              }}
            >
              {t('sidebar.retry')}
            </DropdownMenuItem>
            {createAccountItem}
          </>
        )}

        {loadState === 'loaded' && (
          <>
            {hasAccountChoice ? (
              <>
                <DropdownMenuLabel id={accountsLabelId} className="text-xs text-muted-foreground">
                  {t('sidebar.accounts')}
                </DropdownMenuLabel>
                <DropdownMenuGroup aria-labelledby={accountsLabelId} className="max-h-64 overflow-y-auto">
                  {activeAccounts.map((item) => (
                    <DropdownMenuItem
                      key={item.id}
                      data-testid="scope-switcher-account"
                      aria-current={item.id === account?.id ? 'true' : undefined}
                      ref={item.id === account?.id ? currentAccountRef : undefined}
                      className="cursor-pointer"
                      onSelect={() => handleAccountSelect(item)}
                    >
                      <span className="w-4 mr-2 flex-shrink-0 flex items-center justify-center">
                        {item.id === account?.id && <Check className="w-4 h-4" />}
                      </span>
                      <span className="truncate" title={item.name}>
                        {item.name}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            ) : (
              <DropdownMenuItem disabled className="text-xs text-muted-foreground data-[disabled]:opacity-100" data-testid="scope-switcher-no-other-accounts">
                {t('sidebar.noOtherAccounts')}
              </DropdownMenuItem>
            )}

            {createAccountItem}

            {canChangeAccountHolder && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="scope-switcher-change-holder"
                  className="cursor-pointer"
                  onSelect={handleChangeAccountHolder}
                >
                  <Users className="w-4 h-4 mr-2 flex-shrink-0" />
                  {t('sidebar.changeAccountHolder')}
                </DropdownMenuItem>
              </>
            )}

            {holdersUnknown && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled className="text-xs text-destructive data-[disabled]:opacity-100" data-testid="scope-switcher-holders-error">
                  {t('sidebar.holdersError')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="scope-switcher-holders-retry"
                  className="cursor-pointer"
                  onSelect={(event) => {
                    event.preventDefault();
                    retryHolders();
                  }}
                >
                  {t('sidebar.retry')}
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
